import type { ReactNode } from 'react';
import { Outlet } from 'react-router-dom';

import Logo from '@/assets/svgs/logo.svg?react';
import User from '@/assets/svgs/user.svg?react';
import type { MenuItem } from '@/components/layout/navigation-menu';
import { NavigationMenuItem } from '@/components/layout/navigation-menu-item';
import { Text } from '@/components/Text';
import { Toaster } from '@/components/ui/sonner';
import { useAuthStore } from '@/store/auth';

// Super admin only menu — not driven by API permissions
const ADMIN_MENU_ITEMS: MenuItem[] = [
  { name: 'fmi-users', label: 'Impersonate', path: '/impersonation' },
  { name: 'settings', label: 'App Configuration', path: '/app-configuration' },
  { name: 'roles', label: 'Create Resource', path: '/resources/create' },
];

/**
 * Sidebar shell shared by the super admin only pages.
 */
type Props = {
  children?: ReactNode;
};

export const AdminLayout = ({ children }: Props) => {
  // Component stores
  const { user } = useAuthStore();

  return (
    <>
      <div className='flex flex-col sm:flex-row sm:h-screen'>
        {/* Sidebar */}
        <div className='sticky top-0 z-50 shrink-0 bg-greyDark w-screen sm:w-64 sm:h-screen px-4 pt-4 sm:pt-6 flex flex-col justify-between'>
          <div className='pb-4 sm:pb-0 flex flex-col'>
            <Logo className='w-53 h-10' />

            <div className='hidden sm:block w-full mt-8'>
              {ADMIN_MENU_ITEMS.map((item) => (
                <NavigationMenuItem
                  key={item.name}
                  item={item}
                  closeMenu={() => {}}
                />
              ))}

              <div className='w-full border-t border-white_20 mt-3 pt-3'>
                <NavigationMenuItem
                  item={{
                    name: 'sign-out',
                    label: 'Sign Out',
                    path: '/sign-out',
                  }}
                  closeMenu={() => {}}
                />
              </div>
            </div>
          </div>

          <div className='hidden sm:flex items-center gap-3 pb-6'>
            <div className='shrink-0 w-10 h-10 bg-white rounded-full flex justify-center items-center'>
              <User className='size-6! text-greyDark_60' />
            </div>

            <div className='flex flex-col justify-between min-w-0'>
              <Text renderAs='psmallbold' className='text-white'>
                {user?.firstName} {user?.lastName}
              </Text>
              <Text renderAs='psmall' className='text-white wrap-break-word'>
                {user?.email}
              </Text>
            </div>
          </div>
        </div>

        {/* Page content */}
        <div className='scrollable-container sm:overflow-y-auto flex-1 p-4 sm:p-8'>
          {children ?? <Outlet />}
        </div>
      </div>

      <Toaster />
    </>
  );
};
